import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Redirect, RouteComponentProps, withRouter } from 'react-router-dom';
import { compose, Dispatch } from 'redux';

import { cloneAlphaFromSimulate } from '../../actions/EditorActions/EditorActions';
import { DispatchConnect } from '../../types';
import {
  getCurrentlyViewedStep,
  getLastPageSlug,
  getMaxUnlockedTutorialStep,
  getTutorialIsExpired,
  getTutorialStartDate,
  getTutorialStatus,
} from '../../reducers/TutorialReducer/TutorialSelectors';
import { useTutorialPageContent, useTutorialSteps } from '../../shared/api/apiHooks';
import { Icon } from 'semantic-ui-react';
import { LearnContent } from '../../components/Learn/LearnDetailsContent/LearnContent';
import Immutable from 'immutable';
import { LearnBlockResponse, TutorialStateResponse } from '../../shared/api/api';

import './Tutorial.scss';
import { MemoizedTutorialStatusSection } from './TutorialStatusSection';
import { TutorialButtons } from './TutorialButtons';
import { toggleSimulationFilterThunk } from '../../actions/Simulation/SimulationFiltersActions';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { TutorialActions, TutorialAsyncActions } from '../../reducers/TutorialReducer/TutorialReducer';
import moment from 'moment';
import analytics from '../../shared/services/analytics/analytics';
import { Loader } from '../../components/Loader';
import { TutorialLearn2Quant } from './TutorialL2Q';
import {
  getIsLoggedInSelector,
  getUserPermissionSet,
  hasTutorialPermission,
} from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { SIMULATE_LEARN } from '../../shared/constants';
import { ConsultantTutorial } from './Consultant/ConsultantTutorial';
import { TutorialTask } from './TutorialTask';
import { useAppDispatch } from '../../shared/services/store/useAppDispatch';

interface LearnProps {
  fullWidth: boolean;
  currentActiveTab: number;
  isMultiTab: boolean;
  currentChildActiveTab: number;
  cloneAlphaSimulate: boolean;
  checkIfTabIsEmptyCode: (tabIndex: number, tabIndexChild?: number | undefined) => boolean;
}

interface LearnStateDispatchers {
  dispatchChangeTabCode: (newValue: any, currentTabIndex: number, childActiveTab: number) => void;
}

type LearnSimulateGeneric = LearnProps & LearnStateDispatchers & RouteComponentProps<{}>;

export function TutorialStartPage() {
  const dispatch = useDispatch();
  const handleStartTutorial = () => dispatch(TutorialActions.startTutorial());

  return (
    <div className="tutorial-start">
      <h1>
        <Icon name="book" color="green" />
        <FormattedMessageHelper id="navigatorTutorial.tutorialTitle" />
      </h1>
      <p>
        <FormattedMessageHelper id="navigatorTutorial.startText" />
      </p>
      <p>
        <FormattedMessageHelper id="navigatorTutorial.timeLimit" />
      </p>
      <p>
        <FormattedMessageHelper id="navigatorTutorial.startYourJourney" />
      </p>
      <div className="tutorial-start__button-container">
        <button className="ui button primary" onClick={handleStartTutorial}>
          <FormattedMessageHelper tagName="span" id="navigatorTutorial.startTutorial" />
        </button>
      </div>
    </div>
  );
}

export function ExpiredTutorialScreen() {
  const dispatch = useAppDispatch();
  const startDate = useSelector(getTutorialStartDate);
  const expirationDate = moment(startDate).add(30, 'day').format('MMMM Do YYYY');
  const handleRestartTutorial = () => dispatch(TutorialAsyncActions.restartTutorial());

  return (
    <>
      <h1>
        <FormattedMessageHelper id="navigatorTutorial.tutorialExpired" />
      </h1>
      <p>
        <FormattedMessageHelper id="navigatorTutorial.tutorialExpiredText" values={{ expirationDate }} />
      </p>
      <p>
        <FormattedMessageHelper id="navigatorTutorial.youCanRestart" />
      </p>
      <div>
        <button className="ui button primary" onClick={handleRestartTutorial}>
          <FormattedMessageHelper tagName="span" id="navigatorTutorial.restartTutorial" />
        </button>
      </div>
    </>
  );
}

interface TutorialScreenProps {
  currentActiveTab: number;
  isMultiTab: boolean;
  currentChildActiveTab: number;
  checkIfTabIsEmptyCode: (tabIndex: number, tabIndexChild?: number | undefined) => boolean;
  history: any;
}

export function TutorialContent({
  currentActiveTab,
  isMultiTab,
  currentChildActiveTab,
  checkIfTabIsEmptyCode,
  history,
}: TutorialScreenProps) {
  const currentPage = useSelector(getCurrentlyViewedStep);
  const lastPageSlug = useSelector(getLastPageSlug) as string;
  const lastUnlockedTutorialStep = useSelector(getMaxUnlockedTutorialStep);
  const dispatch = useAppDispatch();
  const { isFetching: isFetchingSteps, data: steps } = useTutorialSteps();

  const tutorialPageSlug = currentPage === steps?.length ? lastPageSlug : steps?.[currentPage]?.slug;
  const { isFetching, data: tutorialContent } = useTutorialPageContent(tutorialPageSlug);

  const immutableContent = Immutable.fromJS(tutorialContent?.content ?? []) as Immutable.List<LearnBlockResponse>;

  const handleSimulateClone = useCallback(
    (alpha: any) => {
      const childActiveTab = isMultiTab ? currentChildActiveTab : -1;
      dispatch(cloneAlphaFromSimulate(alpha.toJS(), currentActiveTab, childActiveTab));
    },
    [currentActiveTab, currentChildActiveTab, dispatch, isMultiTab]
  );

  const handleShowModal = useCallback(() => {
    const childActiveTab = isMultiTab ? currentChildActiveTab : -1;
    let showModal = false;

    if (childActiveTab !== -1) {
      showModal = !checkIfTabIsEmptyCode(currentActiveTab, childActiveTab);
    } else {
      showModal = !checkIfTabIsEmptyCode(currentActiveTab);
    }

    return showModal;
  }, [checkIfTabIsEmptyCode, currentActiveTab, currentChildActiveTab, isMultiTab]);

  const handleFinishTutorial = () => {
    analytics.trackCustomEvent('tutorial_click_finish');
    dispatch(toggleSimulationFilterThunk('learn', currentActiveTab, history));
  };

  const isComplete = currentPage < lastUnlockedTutorialStep;

  const currentTask = steps?.[currentPage]?.task;
  return (
    <>
      <MemoizedTutorialStatusSection />
      {isFetching || isFetchingSteps || !tutorialContent ? (
        <Loader size={'massive'} />
      ) : (
        <>
          {currentTask && <TutorialTask task={currentTask} isComplete={isComplete} />}
          {tutorialContent.title && <h1>{tutorialContent.title}</h1>}
          <LearnContent item={immutableContent} handleSimulateClone={handleSimulateClone} handleShowModal={handleShowModal} />
          {currentTask && <TutorialTask task={currentTask} isComplete={isComplete} />}
        </>
      )}
      <TutorialButtons onFinishTutorial={handleFinishTutorial} />
    </>
  );
}

const Tutorial = ({
  fullWidth,
  currentActiveTab,
  isMultiTab,
  currentChildActiveTab,
  checkIfTabIsEmptyCode,
  history,
}: LearnSimulateGeneric) => {
  //todo: remove after testing
  const dispatch = useDispatch();
  useEffect(() => {
    // @ts-expect-error window property not declared in global type
    window.debugSetTutorialState = (state: TutorialStateResponse) => {
      dispatch(TutorialActions.setState(state));
    };
  }, []);

  const permissionSet = useSelector(getUserPermissionSet);
  const isTutorialStarted = typeof useSelector(getTutorialStartDate) === 'string';
  const isTutorialExpired = useSelector(getTutorialIsExpired);
  const tutorialStatus = useSelector(getTutorialStatus);
  const isConsultant = permissionSet.has('CONSULTANT');
  const isTutorialAvailable = useSelector(hasTutorialPermission) || isConsultant;
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const renderContent = React.useCallback(() => {
    if (isLoggedIn && !isTutorialAvailable) {
      return <Redirect to={SIMULATE_LEARN} />;
    }
    if (isConsultant) {
      return (
        <ConsultantTutorial
          fullWidth={fullWidth}
          currentActiveTab={currentActiveTab}
          isMultiTab={isMultiTab}
          currentChildActiveTab={currentChildActiveTab}
          checkIfTabIsEmptyCode={checkIfTabIsEmptyCode}
          history={history}
        />
      );
    }
    if (tutorialStatus === 'LOADING') {
      return <Loader size={'massive'} />;
    }
    if (!isTutorialStarted) {
      return <TutorialStartPage />;
    }

    if (isTutorialExpired) {
      return <ExpiredTutorialScreen />;
    }

    return (
      <>
        <TutorialContent
          currentActiveTab={currentActiveTab}
          isMultiTab={isMultiTab}
          currentChildActiveTab={currentChildActiveTab}
          checkIfTabIsEmptyCode={checkIfTabIsEmptyCode}
          history={history}
        />
        <TutorialLearn2Quant />
      </>
    );
  }, [isTutorialStarted, isTutorialExpired, currentActiveTab, isMultiTab, currentChildActiveTab, checkIfTabIsEmptyCode, history]);

  return (
    <div className={`tutorial-simulate ${fullWidth ? 'tutorial-simulate--full-width' : ''}`}>
      <div className="tutorial-background">{renderContent()}</div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<any>): LearnStateDispatchers => ({
  dispatchChangeTabCode: (newValue: any, currentTabIndex: number, childActiveTab: number) => {
    dispatch(cloneAlphaFromSimulate(newValue, currentTabIndex, childActiveTab));
  },
});

export default compose<any>(withRouter, connect<{}, DispatchConnect<LearnStateDispatchers>>(null, mapDispatchToProps))(Tutorial);
